import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Stepper } from "../../../../ui/components/Stepper";
import { Button } from "../../../../ui/components/Button";
import { FeatherArrowLeft } from "@subframe/core";
import { Step1DataSource } from "./pivotBuilder/components/Step1DataSource";
import { Step3Results } from "./pivotBuilder/components/Step3Results";
import { FieldListSidebar } from "./pivotBuilder/components/FieldListSidebar";
import { PivotBuilderCanvas } from "./pivotBuilder/components/PivotBuilderCanvas";
import { PivotViewTabs } from "./pivotBuilder/components/PivotViewTabs";
import { CreatePivotViewModal } from "./pivotBuilder/components/CreatePivotViewModal";
import { SavePivotViewModal } from "./pivotBuilder/components/SavePivotViewModal";
import { SaveAllTabsModal } from "./pivotBuilder/components/SaveAllTabsModal";
import {
  ExcelColumn,
  PivotField,
  PivotConfig,
  DateGrouping,
  ExcelData,
  PivotTableData,
  PivotView,
  SavedPivotView,
} from "./pivotBuilder/types";
import { mockDimensions, mockMetrics, mockExcelData } from "./pivotBuilder/mockData";
import { savePivotView as savePivotViewLocal } from "./pivotBuilder/utils/storage";
import { generatePivotTable, savePivotView as savePivotViewApi, getUploadedFile, updatePivotView, getPivotViews, updatePivotSetName, updatePivotViewLastUsed } from "../../data/api/pivotApi";
import { useAuth } from "../../../auth";

type Step = 1 | 2 | 3;

interface CreatePivotBuilderPageProps {
  editViewId?: string; // If provided, we're editing an existing view
  editViewData?: SavedPivotView; // Saved view data when editing
}

export const CreatePivotBuilderPage: React.FC<CreatePivotBuilderPageProps> = ({ editViewId, editViewData }) => {
  const navigate = useNavigate();
  const { fileId: urlFileId } = useParams<{ fileId?: string }>();
  const [searchParams] = useSearchParams();
  const { user, company } = useAuth();
  
  // Get pre-selected data from URL search params (from "Create New Pivot View" flow)
  const preselectedFileId = searchParams.get("fileId");
  const preselectedColumns = searchParams.get("columns");
  const parentViewId = searchParams.get("parentViewId");
  
  // "Add to Register" mode params (from ApplyPivotViewFlow)
  const dataFileId = searchParams.get("dataFileId"); // The uploaded data file
  const newColumnIdsParam = searchParams.get("newColumnIds"); // Columns to highlight as NEW
  const parentFileId = searchParams.get("parentFileId"); // Register set's file_id for loading existing tabs
  const mode = searchParams.get("mode"); // "add-to-register" mode flag
  const periodFromUrl = searchParams.get("period"); // Period from ApplyPivotViewFlow
  const leadFromUrl = searchParams.get("lead"); // Lead from ApplyPivotViewFlow
  const isAddToRegisterMode = mode === "add-to-register" && !!parentFileId;
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [excelData, setExcelData] = useState<(ExcelData & { fileId?: string; columns?: ExcelColumn[] }) | null>(null);
  const [excelColumns, setExcelColumns] = useState<ExcelColumn[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedLead, setSelectedLead] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  // Multiple views management
  const [pivotViews, setPivotViews] = useState<PivotView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showSaveAllModal, setShowSaveAllModal] = useState<boolean>(false);
  const [registerSetName, setRegisterSetName] = useState<string>("");
  
  const [pivotTableData, setPivotTableData] = useState<Record<string, PivotTableData>>({});

  // Pending action states (for save-then-action flows)
  const [pendingViewResults, setPendingViewResults] = useState<boolean>(false);
  const [pendingNewTabName, setPendingNewTabName] = useState<string | null>(null);

  // Get active view
  const activeView = pivotViews.find((v) => v.id === activeViewId);
  const activeConfig = activeView?.config || {
    globalFilters: [],
    rowGroups: [],
    columnLabels: [],
    values: [],
  };

  // Generate tab name
  const generateTabName = (): string => {
    const usedNumbers = pivotViews
      .map((v) => {
        if (!v.name || typeof v.name !== 'string') return null;
        const match = v.name.match(/^Tab (\d+)$/);
        return match ? parseInt(match[1]) : null;
      })
      .filter((n) => n !== null) as number[];
    const nextNumber = usedNumbers.length > 0 ? Math.max(...usedNumbers) + 1 : 1;
    return `Tab ${nextNumber}`;
  };

  // Generate descriptive name based on pivot configuration
  const generateDescriptiveName = (config: PivotConfig): string => {
    const parts: string[] = [];

    // Add row groups
    if (config.rowGroups.length > 0) {
      const rowGroupNames = config.rowGroups.map((rg) => rg.name).join(", ");
      parts.push(`By ${rowGroupNames}`);
    }

    // Add values
    if (config.values.length > 0) {
      const valueNames = config.values.map((v) => v.name).join(", ");
      parts.push(valueNames);
    }

    // If we have a descriptive name, use it; otherwise fall back to Tab N
    if (parts.length > 0) {
      return parts.join(" - ");
    }

    return generateTabName();
  };

  // Load file data if fileId is in URL (either from path param or search param)
  useEffect(() => {
    const loadFileFromUrl = async () => {
      const fileIdToLoad = urlFileId || preselectedFileId;
      
      if (fileIdToLoad && !excelData) {
        try {
          const companyIdInt = company?.companyId ? parseInt(company.companyId) : undefined;
          const fileData = await getUploadedFile(fileIdToLoad, companyIdInt);
          
          // If we have preselected columns from URL, filter to only show those
          let columnsToUse = fileData.columns;
          if (preselectedColumns) {
            try {
              const selectedColumnIds: string[] = JSON.parse(preselectedColumns);
              // Filter columns to only include preselected ones, but keep all available for reference
              const preselectedSet = new Set(selectedColumnIds);
              // Mark columns as preselected (we'll use all columns but highlight preselected ones)
              columnsToUse = fileData.columns.map(col => ({
                ...col,
                isPreselected: preselectedSet.has(col.id),
              }));
            } catch (e) {
              console.warn("Failed to parse preselected columns:", e);
            }
          }
          
          setExcelData({
            headers: fileData.headers,
            rows: [], // No sample rows from this endpoint
            fileId: fileData.fileId,
            columns: columnsToUse,
          });
          setExcelColumns(columnsToUse);
          if (fileData.period) setSelectedPeriod(fileData.period);
          if (fileData.lead) setSelectedLead(fileData.lead);
          setCurrentStep(2); // Go directly to configure step
        } catch (error: any) {
          console.error("Failed to load file from URL:", error);
          alert(`Failed to load file: ${error.message || "Unknown error"}`);
          // Navigate back to create page without fileId
          navigate("/cfo/registers/uploadv1/create", { replace: true });
        }
      }
    };

    loadFileFromUrl();
  }, [urlFileId, preselectedFileId, preselectedColumns, excelData, company?.companyId, navigate]);

  // Handle "Add to Register" mode - load existing tabs and file data with new columns highlighted
  useEffect(() => {
    const loadAddToRegisterMode = async () => {
      if (!isAddToRegisterMode || !dataFileId || !parentFileId || excelData) return;
      
      try {
        const companyIdInt = company?.companyId ? parseInt(company.companyId) : undefined;
        
        // 1. Load file data from the uploaded file
        const fileData = await getUploadedFile(dataFileId, companyIdInt);
        
        // 2. Parse new column IDs and mark them
        let newColumnIds: string[] = [];
        if (newColumnIdsParam) {
          try {
            newColumnIds = JSON.parse(newColumnIdsParam);
          } catch (e) {
            console.warn("Failed to parse newColumnIds:", e);
          }
        }
        const newColumnIdSet = new Set(newColumnIds);
        
        // Mark new columns with isPreselected flag
        const columnsWithHighlight = fileData.columns.map(col => ({
          ...col,
          isPreselected: newColumnIdSet.has(col.id),
        }));
        
        // 3. Set excel data with highlighted columns
        setExcelData({
          headers: fileData.headers,
          rows: [],
          fileId: fileData.fileId,
          columns: columnsWithHighlight,
        });
        setExcelColumns(columnsWithHighlight);
        // Use period/lead from URL (user's selection) if available, otherwise fall back to file metadata
        if (periodFromUrl) {
          setSelectedPeriod(periodFromUrl);
        } else if (fileData.period) {
          setSelectedPeriod(fileData.period);
        }
        if (leadFromUrl) {
          setSelectedLead(leadFromUrl);
        } else if (fileData.lead) {
          setSelectedLead(fileData.lead);
        }
        
        // 4. Load existing tabs from the register set
        const existingViews = await getPivotViews(companyIdInt, parentFileId);
        
        if (existingViews.length > 0) {
          // Sort by tabIndex and convert to PivotView format
          const sortedViews = existingViews.sort((a, b) => (a.tabIndex || 0) - (b.tabIndex || 0));
          const loadedViews: PivotView[] = sortedViews.map((view) => ({
            id: view.id,
            name: view.name,
            config: view.config,
            createdAt: new Date(view.createdAt),
            updatedAt: new Date(view.updatedAt),
            savedViewId: view.id,
            isSaved: true,
          }));
          
          // 5. Create a new empty tab for the user to configure with new columns
          const newTabName = `Tab ${loadedViews.length + 1}`;
          const newView: PivotView = {
            id: `view-${Date.now()}`,
            name: newTabName,
            config: {
              globalFilters: [],
              rowGroups: [],
              columnLabels: [],
              values: [],
            },
            createdAt: new Date(),
            updatedAt: new Date(),
            savedViewId: undefined,
            isSaved: false,
          };
          
          // Add the new tab to existing views
          const allViews = [...loadedViews, newView];
          setPivotViews(allViews);
          setActiveViewId(newView.id); // Activate the new tab
          
          // Preserve register set name from existing views
          // Priority: setName field > first view's name
          const existingSetName = existingViews.find(v => v.setName && v.setName.trim())?.setName;
          setRegisterSetName(existingSetName || existingViews[0]?.name || 'Register');
        } else {
          // No existing views, create first tab
          const firstView: PivotView = {
            id: `view-${Date.now()}`,
            name: "Tab 1",
            config: {
              globalFilters: [],
              rowGroups: [],
              columnLabels: [],
              values: [],
            },
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          setPivotViews([firstView]);
          setActiveViewId(firstView.id);
        }
        
        // 6. Skip to configure step
        setCurrentStep(2);
        
      } catch (error: any) {
        console.error("Failed to load add-to-register mode:", error);
        alert(`Failed to load data: ${error.message || "Unknown error"}`);
        navigate("/cfo/registers/uploadv1", { replace: true });
      }
    };
    
    loadAddToRegisterMode();
  }, [isAddToRegisterMode, dataFileId, parentFileId, newColumnIdsParam, excelData, company?.companyId, navigate]);

  // Load saved view configuration when editing - load ALL tabs from same file
  useEffect(() => {
    const loadEditView = async () => {
      if (editViewId && editViewData && pivotViews.length === 0) {
        try {
          // Load all views from the same file (if fileId exists)
          let allViews: SavedPivotView[] = [editViewData];
          
          if (editViewData.fileId) {
            // Load all tabs from the same file
            const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
            const relatedViews = await getPivotViews(companyId, editViewData.fileId);
            if (relatedViews.length > 0) {
              allViews = relatedViews; // Use all views from the file
            }
          }
          
          // Convert all SavedPivotView to PivotView format and sort by tabIndex
          const sortedViews = allViews.sort((a, b) => (a.tabIndex || 0) - (b.tabIndex || 0));
          const editViews: PivotView[] = sortedViews.map((view) => ({
            id: view.id,
            name: view.name,
            config: view.config,
            createdAt: new Date(view.createdAt),
            updatedAt: new Date(view.updatedAt),
            savedViewId: view.id, // Mark as saved
            isSaved: true,
          }));
          
          setPivotViews(editViews);
          setActiveViewId(editViews[0].id); // Activate first tab
          
          // Load columns from columnMappings (use first view's mappings)
          if (editViewData.columnMappings && editViewData.columnMappings.length > 0) {
            const columns: ExcelColumn[] = editViewData.columnMappings.map((mapping, index) => ({
              id: mapping.columnId,
              name: mapping.originalName,
              type: mapping.type,
              category: mapping.category,
              index: index,
            }));
            setExcelColumns(columns);
            
            // Create mock ExcelData for display (we don't have the actual file data)
            setExcelData({
              headers: columns.map(c => c.name),
              rows: [],
              columns: columns,
              fileId: editViewData.fileId, // Preserve fileId
            });
            
            // Pre-fill period and lead if available
            if (editViewData.lastPeriod) setSelectedPeriod(editViewData.lastPeriod);
            if (editViewData.lastLead) setSelectedLead(editViewData.lastLead);
            
            // Go directly to configure step
            setCurrentStep(2);
          }
        } catch (error: any) {
          console.error("Failed to load related views:", error);
          // Fallback to single view if loading related views fails
          const editView: PivotView = {
            id: editViewData.id,
            name: editViewData.name,
            config: editViewData.config,
            createdAt: new Date(editViewData.createdAt),
            updatedAt: new Date(editViewData.updatedAt),
          };
          setPivotViews([editView]);
          setActiveViewId(editView.id);
        }
      }
    };
    
    loadEditView();
  }, [editViewId, editViewData, pivotViews.length, company?.companyId]);

  // Initialize first view when data is selected (for new views, not editing)
  useEffect(() => {
    if (!editViewId && excelData && pivotViews.length === 0) {
      const firstView: PivotView = {
        id: `view-${Date.now()}`,
        name: generateTabName(),
        config: {
          globalFilters: [],
          rowGroups: [],
          columnLabels: [],
          values: [],
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        savedViewId: undefined,
        isSaved: false,
      };
      setPivotViews([firstView]);
      setActiveViewId(firstView.id);
    }
  }, [editViewId, excelData, pivotViews.length]);

  const generateFieldId = (columnId: string) => {
    return `${columnId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleDataSelected = (data: ExcelData & { fileId?: string; columns?: ExcelColumn[] }, period: string, lead: string, leadLevelId: number | null) => {
    setExcelData(data);
    setExcelColumns(data.columns || []);
    setSelectedPeriod(period);
    setSelectedLead(lead);
    
    // Update URL to include fileId if available
    if (data.fileId) {
      navigate(`/cfo/registers/uploadv1/create/${data.fileId}`, { replace: true });
    }
    
    setCurrentStep(2);
  };

  const handleAddField = (
    zone: keyof PivotConfig,
    field: ExcelColumn,
    options?: { dateGrouping?: DateGrouping; aggregation?: string; filterValues?: string[] }
  ) => {
    if (!activeView) return;

    const newField: PivotField = {
      id: generateFieldId(field.id),
      columnId: field.id,
      name: field.name,
      type: field.type,
      dateGrouping: options?.dateGrouping || undefined,
      aggregation: (options?.aggregation as any) || (field.type === "number" ? "sum" : undefined),
      filterValues: options?.filterValues || undefined,
    };

    const updatedConfig: PivotConfig = {
      ...activeConfig,
      [zone]: [...activeConfig[zone], newField],
    };

    updateActiveViewConfig(updatedConfig);
  };

  const handleRemoveField = (zone: keyof PivotConfig, fieldId: string) => {
    if (!activeView) return;

    const updatedConfig: PivotConfig = {
      ...activeConfig,
      [zone]: activeConfig[zone].filter((f) => f.id !== fieldId),
    };

    updateActiveViewConfig(updatedConfig);
  };

  const handleUpdateDateGrouping = (fieldId: string, grouping: DateGrouping) => {
    if (!activeView) return;

    const updatedConfig: PivotConfig = {
      ...activeConfig,
      columnLabels: activeConfig.columnLabels.map((f) =>
        f.id === fieldId ? { ...f, dateGrouping: grouping } : f
      ),
    };

    updateActiveViewConfig(updatedConfig);
  };

  const handleUpdateAggregation = (fieldId: string, aggregation: string) => {
    if (!activeView) return;

    const updatedConfig: PivotConfig = {
      ...activeConfig,
      values: activeConfig.values.map((f) =>
        f.id === fieldId ? { ...f, aggregation: aggregation as any } : f
      ),
    };

    updateActiveViewConfig(updatedConfig);
  };

  const handleUpdateFilterValues = (fieldId: string, values: string[]) => {
    if (!activeView) return;

    const updatedConfig: PivotConfig = {
      ...activeConfig,
      globalFilters: activeConfig.globalFilters.map((f) =>
        f.id === fieldId ? { ...f, filterValues: values } : f
      ),
    };

    updateActiveViewConfig(updatedConfig);
  };

  const updateActiveViewConfig = (config: PivotConfig) => {
    setPivotViews((prev) =>
      prev.map((v) =>
        v.id === activeViewId
          ? { ...v, config, updatedAt: new Date() }
          : v
      )
    );
    generatePreview(config).catch(console.error);
  };

  const generatePreview = async (config?: PivotConfig) => {
    const configToUse = config || activeConfig;
    
    if (configToUse.rowGroups.length === 0 && configToUse.values.length === 0) {
      setPivotTableData((prev) => ({
        ...prev,
        [activeViewId]: null as any,
      }));
      return;
    }

    // If no fileId, use mock data
    if (!excelData?.fileId) {
      generateMockPreview(configToUse);
      return;
    }

    setIsGenerating(true);

    try {
      // Call backend API to generate pivot table
      const pivotData = await generatePivotTable(
        excelData.fileId,
        configToUse,
        selectedPeriod,
        selectedLead
      );

      setPivotTableData((prev) => ({
        ...prev,
        [activeViewId]: pivotData,
      }));
    } catch (error: any) {
      console.error("Failed to generate pivot table:", error);
      alert(`Failed to generate pivot table: ${error.message || "Unknown error"}`);
      // Fallback to mock data on error
      generateMockPreview(configToUse);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateMockPreview = (configToUse: PivotConfig) => {
    const rowHeaders = configToUse.rowGroups.map((f) => f.name);
    const colHeaders = configToUse.columnLabels.map((f) => f.name);
    const valueFields = configToUse.values.map((f) => f.name);

    const sampleRows = [
      ["Apollo Hospitals Enterprise Ltd", "Domestic"],
      ["Hexaware Technologies Ltd", "Domestic"],
      ["NEULAND LABORATORIES LIMITED", "Domestic"],
    ].slice(0, Math.max(1, configToUse.rowGroups.length));

    const sampleCols = colHeaders.length > 0
      ? ["Jan-2025", "Feb-2025", "Mar-2025"]
      : valueFields;

    const sampleData = sampleRows.map(() =>
      sampleCols.map(() => Math.floor(Math.random() * 1000000))
    );

    setPivotTableData((prev) => ({
      ...prev,
      [activeViewId]: {
        rows: sampleRows,
        columns: sampleCols,
        data: sampleData,
      },
    }));
  };

  // Auto-save current tab with default name
  const autoSaveCurrentTab = async (): Promise<boolean> => {
    if (!activeView || !excelData || !excelColumns || excelColumns.length === 0) {
      console.warn("Cannot auto-save: Missing required data");
      return false;
    }

    // Check if already saved
    if (activeView.savedViewId && activeView.isSaved) {
      // Update existing saved view silently
      try {
        setIsSaving(true);
        const columnMappings = excelColumns.map((col) => ({
          originalName: col.name,
          columnId: col.id,
          type: col.type,
          category: col.category,
        }));

        const userId = user?.id ? parseInt(user.id) : 1;
        const companyId = company?.companyId ? parseInt(company.companyId) : undefined;

        const updateData = {
          name: activeView.name,
          columnMappings,
          config: activeConfig,
          lastPeriod: selectedPeriod || undefined,
          lastLead: selectedLead || undefined,
          fileId: excelData.fileId || undefined,
          tabIndex: pivotViews.findIndex((v) => v.id === activeViewId) + 1,
        };

        const savedView = await updatePivotView(activeView.savedViewId, updateData, userId, companyId);
        savePivotViewLocal(savedView);

        // Update local state
        setPivotViews((prev) =>
          prev.map((v) =>
            v.id === activeViewId
              ? { ...v, updatedAt: new Date(), savedViewId: savedView.id, isSaved: true }
              : v
          )
        );

        return true;
      } catch (error: any) {
        console.error("Failed to auto-update pivot view:", error);
        // Don't block the flow if update fails
        return true; // Still proceed
      } finally {
        setIsSaving(false);
      }
    }

    // Create new saved view
    try {
      setIsSaving(true);

      const columnMappings = excelColumns.map((col) => ({
        originalName: col.name,
        columnId: col.id,
        type: col.type,
        category: col.category,
      }));

      const userId = user?.id ? parseInt(user.id) : 1;
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;

      // Generate descriptive name or use existing tab name
      const viewName = activeView.name || generateDescriptiveName(activeConfig);
      
      // Update local view name if it was auto-generated
      if (!activeView.name || activeView.name.startsWith("Tab ")) {
        setPivotViews((prev) =>
          prev.map((v) => (v.id === activeViewId ? { ...v, name: viewName } : v))
        );
      }

      // In "add to register" mode, use parentFileId to link new tabs to the same register set
      // Otherwise use the current file's ID
      const fileIdToSave = isAddToRegisterMode && parentFileId 
        ? parentFileId  // Link to existing register set
        : (excelData.fileId || undefined);
      const tabIndex = pivotViews.findIndex((v) => v.id === activeViewId) + 1;

      // In "add-to-register" mode, pass the existing setName to ensure new tabs are grouped correctly
      const existingSetName = isAddToRegisterMode && registerSetName && registerSetName.trim()
        ? registerSetName.trim()
        : undefined;

      const viewData = {
        name: viewName,
        fileId: fileIdToSave,
        tabIndex,
        columnMappings,
        config: activeConfig,
        lastPeriod: selectedPeriod || undefined,
        lastLead: selectedLead || undefined,
        setName: existingSetName, // Pass existing setName to maintain register grouping
      };

      const savedView = await savePivotViewApi(viewData, userId, companyId);
      savePivotViewLocal(savedView);

      // Update local state with saved view ID
      setPivotViews((prev) =>
        prev.map((v) =>
          v.id === activeViewId
            ? { ...v, name: viewName, savedViewId: savedView.id, isSaved: true, updatedAt: new Date() }
            : v
        )
      );

      return true;
    } catch (error: any) {
      console.error("Failed to auto-save pivot view:", error);
      // Show error but don't block - user can still view results
      alert(`Warning: Failed to auto-save view: ${error.message || "Unknown error"}. You can save manually later.`);
      return false; // Return false but don't throw
    } finally {
      setIsSaving(false);
    }
  };

  const handleStep2Next = async () => {
    if (activeConfig.values.length === 0) {
      alert("Please add at least one metric to the Values zone.");
      return;
    }

    // Check if ANY tabs are unsaved
    const unsavedTabs = pivotViews.filter(tab => !tab.savedViewId || !tab.isSaved);
    
    if (unsavedTabs.length > 0) {
      // Show save all modal, set pending flag to navigate after save
      setPendingViewResults(true);
      setShowSaveAllModal(true);
      return;
    }

    // All tabs already saved - proceed directly
    setIsGenerating(true);
    try {
      await generatePreview();
      setCurrentStep(3);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStep3Back = () => {
    setCurrentStep(2);
  };

  const handleCreateNewView = () => {
    // Check if current tab has content and is unsaved
    if (activeView && activeConfig.values.length > 0 && (!activeView.savedViewId || !activeView.isSaved)) {
      // Store the pending new tab action and show save modal for current tab
      setPendingNewTabName(""); // Empty string means we'll show create modal after save
      setShowSaveModal(true);
      return;
    }
    
    // Current tab is saved or empty - show create modal directly
    setShowCreateModal(true);
  };

  const createNewViewDirectly = (name: string) => {
    const newView: PivotView = {
      id: `view-${Date.now()}`,
      name: name || generateTabName(),
      config: {
        globalFilters: [],
        rowGroups: [],
        columnLabels: [],
        values: [],
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      savedViewId: undefined,
      isSaved: false,
    };

    setPivotViews((prev) => [...prev, newView]);
    setActiveViewId(newView.id);
    setShowCreateModal(false);
    
    if (currentStep === 3) {
      setCurrentStep(2);
    }
  };

  const handleConfirmCreateView = (name: string) => {
    createNewViewDirectly(name);
  };

  const handleSelectView = async (viewId: string) => {
    setActiveViewId(viewId);
    const view = pivotViews.find((v) => v.id === viewId);
    if (view && !pivotTableData[viewId]) {
      await generatePreview(view.config);
    }
  };

  const handleRenameView = (viewId: string, newName: string) => {
    setPivotViews((prev) =>
      prev.map((v) => (v.id === viewId ? { ...v, name: newName, updatedAt: new Date() } : v))
    );
  };

  const handleDeleteView = (viewId: string) => {
    if (pivotViews.length <= 1) {
      alert("You must have at least one pivot view.");
      return;
    }

    setPivotViews((prev) => {
      const filtered = prev.filter((v) => v.id !== viewId);
      if (viewId === activeViewId && filtered.length > 0) {
        setActiveViewId(filtered[0].id);
      }
      return filtered;
    });

    setPivotTableData((prev) => {
      const updated = { ...prev };
      delete updated[viewId];
      return updated;
    });
  };

  const handleEditView = () => {
    setCurrentStep(2);
  };

  const handleSaveView = () => {
    if (!activeView || !excelData) {
      alert("Cannot save: No active view or Excel data.");
      return;
    }

    if (activeConfig.values.length === 0) {
      alert("Please add at least one metric to the Values zone before saving.");
      return;
    }

    setShowSaveModal(true);
  };

  const handleSaveAllTabs = () => {
    if (!excelData) {
      alert("Cannot save: No Excel data available.");
      return;
    }

    if (pivotViews.length === 0) {
      alert("No tabs to save.");
      return;
    }

    setShowSaveAllModal(true);
  };

  // Auto-save all unsaved tabs
  const autoSaveAllTabs = async (): Promise<{ savedCount: number; savedViewIds: string[]; failureCount: number }> => {
    if (!excelData || !excelColumns || excelColumns.length === 0) {
      console.warn("Cannot auto-save: Missing required data");
      return { savedCount: 0, savedViewIds: [], failureCount: 0 };
    }

    const unsavedTabs = pivotViews.filter(tab => !tab.savedViewId || !tab.isSaved);
    const alreadySavedViewIds = pivotViews
      .filter(tab => tab.savedViewId && tab.isSaved)
      .map(tab => tab.savedViewId!)
      .filter((id): id is string => !!id);

    if (unsavedTabs.length === 0) {
      return { savedCount: 0, savedViewIds: alreadySavedViewIds, failureCount: 0 }; // All tabs already saved
    }

    setIsSaving(true);
    let savedCount = 0;
    let failureCount = 0;
    const newSavedViewIds: string[] = [];

    try {
      const userId = user?.id ? parseInt(user.id) : 1;
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      const columnMappings = excelColumns.map((col) => ({
        originalName: col.name,
        columnId: col.id,
        type: col.type,
        category: col.category,
      }));

      // In "add to register" mode, use parentFileId to link new tabs to the same register set
      const fileIdToSave = isAddToRegisterMode && parentFileId 
        ? parentFileId 
        : (excelData.fileId || undefined);

      // Save each unsaved tab
      for (const tab of unsavedTabs) {
        try {
          const viewName = tab.name || generateDescriptiveName(tab.config);
          const tabIndex = pivotViews.findIndex((v) => v.id === tab.id) + 1;

          // In "add-to-register" mode, pass the existing setName to ensure new tabs are grouped correctly
          const existingSetName = isAddToRegisterMode && registerSetName && registerSetName.trim()
            ? registerSetName.trim()
            : undefined;

          const viewData = {
            name: viewName,
            fileId: fileIdToSave,
            tabIndex,
            columnMappings,
            config: tab.config,
            lastPeriod: selectedPeriod || undefined,
            lastLead: selectedLead || undefined,
            setName: existingSetName, // Pass existing setName to maintain register grouping
          };

          const savedView = await savePivotViewApi(viewData, userId, companyId);
          savePivotViewLocal(savedView);
          newSavedViewIds.push(savedView.id);

          // Update local state
          setPivotViews((prev) =>
            prev.map((v) =>
              v.id === tab.id
                ? { ...v, name: viewName, savedViewId: savedView.id, isSaved: true, updatedAt: new Date() }
                : v
            )
          );

          savedCount++;
        } catch (error: any) {
          console.error(`Failed to auto-save tab ${tab.name}:`, error);
          failureCount++;
          // Continue with other tabs
        }
      }

      // Return all saved view IDs (both already saved and newly saved)
      return { savedCount, savedViewIds: [...alreadySavedViewIds, ...newSavedViewIds], failureCount };
    } catch (error: any) {
      console.error("Failed to auto-save tabs:", error);
      return { savedCount, savedViewIds: [...alreadySavedViewIds, ...newSavedViewIds], failureCount }; // Return what we have so far
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRegisterSetName = async (setName: string, fileId: string) => {
    if (!setName.trim()) return; // Optional field
    
    try {
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;
      await updatePivotSetName(fileId, setName.trim(), companyId);
      setRegisterSetName(setName.trim());
      // Update local pivotViews to reflect new setName
      setPivotViews((prev) =>
        prev.map((v) => (v.fileId === fileId ? { ...v, setName: setName.trim() } : v))
      );
    } catch (error: any) {
      console.error("Failed to update register set name:", error);
      throw error; // Re-throw to let caller handle
    }
  };

  const handleConfirmSaveAll = async (tabNames: Record<string, string>, registerSetName?: string) => {
    if (!excelData || !excelColumns || excelColumns.length === 0) {
      alert("Cannot save: No column information available.");
      return { savedCount: 0, savedViewIds: [], failureCount: 0 };
    }

    setIsSaving(true);
    let savedCount = 0;
    let failureCount = 0;
    const newlySavedViewIds: string[] = [];
    
    // Get already saved view IDs (for export after save)
    const alreadySavedViewIds = pivotViews
      .filter(tab => tab.savedViewId && tab.isSaved)
      .map(tab => tab.savedViewId!)
      .filter((id): id is string => !!id);

    try {
      const userId = user?.id ? parseInt(user.id) : 1;
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;

      // Generate column mappings once
      const columnMappings = excelColumns.map((col) => ({
        originalName: col.name,
        columnId: col.id,
        type: col.type,
        category: col.category,
      }));

      // Save each tab with fileId and tabIndex
      // In "add to register" mode, use parentFileId to link new tabs to the same register set
      const fileId = isAddToRegisterMode && parentFileId 
        ? parentFileId 
        : (excelData.fileId || undefined);
      const savePromises = Object.entries(tabNames).map(async ([tabId, viewName], index) => {
        const tab = pivotViews.find((v) => v.id === tabId);
        if (!tab) return null;

        // Find the original tab index in pivotViews array
        const originalTabIndex = pivotViews.findIndex((v) => v.id === tabId) + 1;

        try {
          let savedView;
          const isUpdate = tab.savedViewId && tab.isSaved;
          
          // Check if tab is already saved - update instead of create
          if (isUpdate) {
            // Update existing saved view
            const updateData = {
              name: viewName,
              config: tab.config,
              lastPeriod: selectedPeriod || undefined,
              lastLead: selectedLead || undefined,
            };
            savedView = await updatePivotView(tab.savedViewId!, updateData, userId, companyId);
            
            // In "add-to-register" mode, also record the data file in file history for updated views
            if (isAddToRegisterMode && dataFileId) {
              try {
                await updatePivotViewLastUsed(
                  savedView.id,
                  selectedPeriod || undefined,
                  selectedLead || undefined,
                  dataFileId  // Record the actual uploaded data file
                );
              } catch (error) {
                console.warn("Failed to record data file in history for updated view:", error);
                // Continue even if this fails
              }
            }
          } else {
            // Create new view
            // In "add-to-register" mode, pass the existing setName to ensure new tabs are grouped correctly
            const existingSetName = isAddToRegisterMode && registerSetName && registerSetName.trim()
              ? registerSetName.trim()
              : undefined;
            
            const viewData = {
              name: viewName,
              fileId: fileId, // Link all tabs from same file
              tabIndex: originalTabIndex, // Order of tab (1, 2, 3...)
              columnMappings,
              config: tab.config,
              lastPeriod: selectedPeriod || undefined,
              lastLead: selectedLead || undefined,
              setName: existingSetName, // Pass existing setName to maintain register grouping
            };
            savedView = await savePivotViewApi(viewData, userId, companyId);
            // Only add newly created view IDs (not updates)
            newlySavedViewIds.push(savedView.id);
          }
          
          savePivotViewLocal(savedView);
          savedCount++;
          return { tabId, savedView, isUpdate };
        } catch (error: any) {
          console.error(`Failed to save tab ${viewName}:`, error);
          failureCount++;
          return null;
        }
      });

      const results = await Promise.all(savePromises);

      // Update local state with saved view IDs
      setPivotViews((prev) =>
        prev.map((view) => {
          const result = results.find((r) => r && r.tabId === view.id);
          if (result) {
            return {
              ...view,
              name: tabNames[view.id] || view.name,
              savedViewId: result.savedView.id,
              isSaved: true,
              updatedAt: new Date(),
            };
          }
          return view;
        })
      );

      // In "add-to-register" mode, record the data file in file history for newly created views
      // This is separate from the fileId used to link tabs (which is parentFileId)
      if (isAddToRegisterMode && dataFileId) {
        const newlyCreatedResults = results.filter(r => r && !r.isUpdate);
        for (const result of newlyCreatedResults) {
          if (result?.savedView?.id) {
            try {
              await updatePivotViewLastUsed(
                result.savedView.id,
                selectedPeriod || undefined,
                selectedLead || undefined,
                dataFileId  // Record the actual uploaded data file, not parentFileId
              );
            } catch (error) {
              console.warn("Failed to record data file in history:", error);
              // Continue even if this fails
            }
          }
        }
      }

      // Update register set name if provided
      if (registerSetName && registerSetName.trim() && fileId) {
        try {
          await handleUpdateRegisterSetName(registerSetName.trim(), fileId);
        } catch (error: any) {
          console.error("Failed to update register set name:", error);
          // Continue even if register name update fails
        }
      }

      setShowSaveAllModal(false);
      
      // Check if there's a pending view results navigation
      if (pendingViewResults) {
        setPendingViewResults(false);
        // Navigate to results after successful save
        setIsGenerating(true);
        try {
          await generatePreview();
          setCurrentStep(3);
        } finally {
          setIsGenerating(false);
        }
        // Return without showing alert (smoother UX for View Results flow)
        const allSavedViewIds = [...alreadySavedViewIds, ...newlySavedViewIds];
        return { savedCount, savedViewIds: allSavedViewIds, failureCount };
      }

      let message = `Successfully saved ${savedCount} register${savedCount !== 1 ? "s" : ""}!`;
      if (registerSetName && registerSetName.trim()) {
        message += ` Register set named as "${registerSetName.trim()}".`;
      }
      if (failureCount > 0) {
        message += ` ${failureCount} register${failureCount !== 1 ? "s" : ""} failed to save.`;
      }
      alert(message);

      // Return ALL saved view IDs (newly saved + already saved) for export
      const allSavedViewIds = [...alreadySavedViewIds, ...newlySavedViewIds];
      return { savedCount, savedViewIds: allSavedViewIds, failureCount };
    } catch (error: any) {
      console.error("Failed to save tabs:", error);
      alert(`Failed to save tabs: ${error.message || "Unknown error"}`);
      // Reset pending state on error
      setPendingViewResults(false);
      // Return ALL saved view IDs we have so far
      const allSavedViewIds = [...alreadySavedViewIds, ...newlySavedViewIds];
      return { savedCount, savedViewIds: allSavedViewIds, failureCount };
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmSave = async (viewName: string) => {
    if (!activeView || !excelData) return;

    // Validate we have columns
    if (!excelColumns || excelColumns.length === 0) {
      alert("Cannot save: No column information available. Please upload a file first.");
      return;
    }

    setIsSaving(true);

    try {
      // Generate column mappings from actual Excel columns
      const columnMappings = excelColumns.map((col) => ({
        originalName: col.name,
        columnId: col.id,
        type: col.type,
        category: col.category,
      }));

      // Get userId and companyId from auth
      const userId = user?.id ? parseInt(user.id) : 1;
      const companyId = company?.companyId ? parseInt(company.companyId) : undefined;

      let savedView: SavedPivotView;

      if (editViewId) {
        // Update existing view
        const updateData = {
          name: viewName,
          columnMappings,
          config: activeConfig,
          lastPeriod: selectedPeriod || undefined,
          lastLead: selectedLead || undefined,
        };
        savedView = await updatePivotView(editViewId, updateData, userId, companyId);
      } else {
        // Create new view
        const fileId = excelData.fileId || undefined;
        const tabIndex = pivotViews.findIndex((v) => v.id === activeViewId) + 1; // 1-based index
        
        const viewData = {
          name: viewName,
          fileId: fileId, // Link to the Excel file
          tabIndex: tabIndex, // Order of this tab
          columnMappings,
          config: activeConfig,
          lastPeriod: selectedPeriod || undefined,
          lastLead: selectedLead || undefined,
        };
        savedView = await savePivotViewApi(viewData, userId, companyId);
      }

      // Also save to localStorage for offline support
      savePivotViewLocal(savedView);

      // Update local state with saved view ID
      setPivotViews((prev) =>
        prev.map((v) =>
          v.id === activeViewId
            ? { ...v, name: viewName, savedViewId: savedView.id, isSaved: true, updatedAt: new Date() }
            : v
        )
      );

      setShowSaveModal(false);

      // Check if there's a pending new tab creation
      if (pendingNewTabName !== null) {
        const pendingName = pendingNewTabName;
        setPendingNewTabName(null);
        // Show create modal for new tab
        setShowCreateModal(true);
        return;
      }

      // Show success message
      alert(`Pivot view "${viewName}" ${editViewId ? "updated" : "saved"} successfully!`);

      // Navigate to results or stay on current page
      if (currentStep === 2) {
        // If on configure screen, generate preview and go to results
        await generatePreview();
        setCurrentStep(3);
      }
      // If already on results (step 3), stay on the page - don't redirect
    } catch (error: any) {
      console.error("Failed to save pivot view:", error);
      alert(`Failed to ${editViewId ? "update" : "save"} pivot view: ${error.message || "Unknown error"}`);
      // Reset pending state on error
      setPendingNewTabName(null);
    } finally {
      setIsSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1DataSource
            onDataSelected={handleDataSelected}
            onNext={() => setCurrentStep(2)}
          />
        );

      case 2:
        if (!activeView) return null;
        
        // Count new columns for the info banner
        const newColumnsCount = excelColumns.filter(c => c.isPreselected).length;
        
        return (
          <div className="flex flex-col h-full">
            {/* Info banner for "add to register" mode */}
            {isAddToRegisterMode && newColumnsCount > 0 && (
              <div className="bg-blue-50 border-b border-blue-200 px-4 py-3 flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Adding to existing register:</span> You have {newColumnsCount} new column{newColumnsCount !== 1 ? 's' : ''} highlighted with <span className="inline-flex items-center px-1.5 py-0.5 text-xs bg-brand-100 text-brand-700 rounded">NEW</span> in the sidebar. 
                    Configure your new tab using these columns, or edit existing tabs.
                  </p>
                </div>
              </div>
            )}
            
            {/* Tabs */}
            <PivotViewTabs
              views={pivotViews}
              activeViewId={activeViewId}
              onSelectView={handleSelectView}
              onRenameView={handleRenameView}
              onDeleteView={handleDeleteView}
              onCreateNew={handleCreateNewView}
            />

            {/* Builder Content */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Sidebar - Field List */}
              <FieldListSidebar
                dimensions={excelColumns.filter(c => c.category === 'dimension')}
                metrics={excelColumns.filter(c => c.category === 'metric')}
              />

              {/* Right Side - Builder Canvas */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-y-auto min-h-0">
                  <PivotBuilderCanvas
                    globalFilters={activeConfig.globalFilters}
                    rowGroups={activeConfig.rowGroups}
                    columnLabels={activeConfig.columnLabels}
                    values={activeConfig.values}
                    excelData={excelData || mockExcelData}
                    onAddField={handleAddField}
                    onRemoveField={handleRemoveField}
                    onUpdateDateGrouping={handleUpdateDateGrouping}
                    onUpdateAggregation={handleUpdateAggregation}
                    onUpdateFilterValues={handleUpdateFilterValues}
                  />
                </div>

                {/* Action Buttons - Fixed at bottom */}
                <div className="flex-shrink-0 p-4 border-t border-neutral-border bg-default-background">
                  <div className="flex justify-between">
                    <Button 
                      variant="neutral-tertiary" 
                      onClick={() => {
                        // In "add to register" mode, go back to ApplyPivotViewFlow
                        if (isAddToRegisterMode && parentFileId) {
                          // Find the first existing view's ID to navigate back
                          const firstExistingView = pivotViews.find(v => v.savedViewId);
                          if (firstExistingView?.savedViewId) {
                            navigate(`/cfo/registers/uploadv1/apply/${firstExistingView.savedViewId}`);
                            return;
                          }
                        }
                        navigate("/cfo/registers/uploadv1");
                      }}
                    >
                      {isAddToRegisterMode ? "Back to Apply" : "Cancel"}
                    </Button>
                    <div className="flex gap-2">
                      <button
                        onClick={handleStep2Next}
                        disabled={activeConfig.values.length === 0 || isGenerating || isSaving}
                        className="px-4 py-2 rounded-md bg-brand-600 text-white font-body-bold disabled:bg-neutral-300 disabled:cursor-not-allowed hover:bg-brand-700 transition-colors flex items-center gap-2"
                      >
                        {isSaving ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Saving...
                          </>
                        ) : isGenerating ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Generating...
                          </>
                        ) : (
                          "View Results"
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        if (!activeView) return null;

        return (
          <div className="flex flex-col h-full">
            {/* Tabs */}
            <div className="flex items-center justify-between border-b border-neutral-border bg-default-background px-4 py-2">
              <PivotViewTabs
                views={pivotViews}
                activeViewId={activeViewId}
                onSelectView={handleSelectView}
                onRenameView={handleRenameView}
                onDeleteView={handleDeleteView}
                onCreateNew={handleCreateNewView}
              />
              <div className="flex items-center gap-2">
              </div>
            </div>

            {/* Results Content */}
            <div className="flex-1 overflow-auto">
              <Step3Results
                pivotConfig={activeConfig}
                pivotData={pivotTableData[activeViewId] || null}
                viewName={activeView?.name || 'Pivot View'}
                viewId={editViewId || (activeView?.savedViewId)}
                fileId={excelData?.fileId}
                allTabs={pivotViews}
                allTabsData={pivotTableData}
                onBack={handleStep3Back}
                onEditView={handleEditView}
                onCreateNewView={handleCreateNewView}
                onSaveAllTabs={handleConfirmSaveAll}
                autoSaveAllTabs={autoSaveAllTabs}
                registerSetName={registerSetName}
                onUpdateRegisterSetName={excelData?.fileId ? async (setName: string) => {
                  if (excelData?.fileId) {
                    await handleUpdateRegisterSetName(setName, excelData.fileId);
                  }
                } : undefined}
                isSaving={isSaving}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header */}
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <div className="flex items-center gap-4">
            <Button variant="neutral-tertiary" icon={<FeatherArrowLeft />} onClick={() => navigate("/cfo/registers/uploadv1")}>
              Back to Library
            </Button>
            <div>
              <h1 className="text-heading-1 font-heading-1 text-default-font">
                {editViewId ? "Edit Pivot View" : "Create New Pivot View"}
              </h1>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="w-full px-8 py-4 border-b border-neutral-border bg-default-background">
          <Stepper>
            <Stepper.Step
              variant={currentStep === 1 ? "active" : currentStep > 1 ? "completed" : "default"}
              stepNumber="1"
              label="Upload Data"
              firstStep
            />
            <Stepper.Step
              variant={currentStep === 2 ? "active" : currentStep > 2 ? "completed" : "default"}
              stepNumber="2"
              label="Configure Pivot"
            />
            <Stepper.Step
              variant={currentStep === 3 ? "active" : "default"}
              stepNumber="3"
              label="View Results"
              lastStep
            />
          </Stepper>
        </div>

        {/* Step Content */}
        <div className="flex-1 w-full overflow-hidden bg-default-background">
          {renderStepContent()}
        </div>
      </div>

      {/* Create View Modal */}
      <CreatePivotViewModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onConfirm={handleConfirmCreateView}
        suggestedName={generateTabName()}
      />

      {/* Save View Modal */}
      <SavePivotViewModal
        isOpen={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setPendingNewTabName(null); // Reset pending new tab on close
        }}
        onConfirm={handleConfirmSave}
        suggestedName={activeView?.name || "My Pivot View"}
        isSaving={isSaving}
      />

      {/* Save All Tabs Modal */}
      <SaveAllTabsModal
        isOpen={showSaveAllModal}
        onClose={() => {
          setShowSaveAllModal(false);
          setPendingViewResults(false); // Reset pending view results on close
        }}
        onConfirm={handleConfirmSaveAll}
        tabs={pivotViews}
        isSaving={isSaving}
        registerName={registerSetName}
      />
    </DefaultPageLayout>
  );
};

